import { useEffect } from "react";

import { BRAND } from "@/brand.config";
import { currentAcquisition, privacySignalsOptOut } from "@/lib/analytics";
import { storeCampaignToken, withStoreCampaign } from "@/lib/store-campaign";

/**
 * Tags a link to the App Store listing with Apple's campaign token at the
 * moment it is pressed — one delegated listener, so every surface that links
 * to `BRAND.appStoreUrl` (the badges, the platform card, the Premium trial
 * button) is covered without any of them knowing. The token is
 * `web-<source>` from src/lib/store-campaign.ts; docs/ops/campaign-links.md is
 * how to read it back in App Store Connect.
 *
 * AT PRESS TIME, NOT RENDER TIME. The site is prerendered, and the server does
 * not know where a visit came from, so a tagged `href` at render would either
 * mismatch hydration or say "direct" for everyone. The capture-phase listener
 * runs before the anchor's own handlers and before the browser follows the
 * link, and the browser reads `href` when it follows it.
 *
 * What it does NOT do:
 *   · run without `BRAND.appStoreProviderToken` — Apple ignores a `ct` with no
 *     `pt`, so an untagged link is the honest link until the owner pastes it;
 *   · run for a visitor with Do Not Track or Global Privacy Control set — they
 *     are not measured at all (analytics.ts), and a campaign label is still a
 *     measurement;
 *   · touch any other Apple link (the offer-code redeem sheet), or a link that
 *     already names its own campaign.
 *
 * `auxclick` as well as `click`, so a middle-click into a new tab is tagged too.
 */
export function useStoreCampaignLinks(): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const listing = BRAND.appStoreUrl;
    const providerToken = BRAND.appStoreProviderToken;
    if (!listing || !providerToken) return;
    if (privacySignalsOptOut()) return;

    const onPress = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      const source = currentAcquisition();
      if (!source) return;
      const tagged = withStoreCampaign(
        href,
        listing,
        providerToken,
        storeCampaignToken(source),
      );
      if (tagged) anchor.setAttribute("href", tagged);
    };

    document.addEventListener("click", onPress, true);
    document.addEventListener("auxclick", onPress, true);
    return () => {
      document.removeEventListener("click", onPress, true);
      document.removeEventListener("auxclick", onPress, true);
    };
  }, []);
}
